import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Place } from '../../../types/interfaces';
import { adminApi } from '../../../redux/adminApiSlice';
import DisplayJSON from '../../../components/DisplayJSON/DisplayJSON';
import { Spreadsheet } from '../../../spreadsheet/src';
import { ColumnDef } from '@tanstack/react-table';
import { useCopyToClipboard, useDeleteWithConfirmation, useSpreadsheetConfig } from '../../../hooks';

// Extend Place interface to include id for Spreadsheet
interface PlaceWithId extends Place {
  id: string;
}

const AllPlacesTab: React.FC = () => {
  const [selectedPlaceForJson, setSelectedPlaceForJson] = useState<Place | null>(null);
  const { data: places = [], isLoading, error, refetch } = adminApi.useGetPlacesQuery();
  const [deleteLocation] = adminApi.useDeleteLocationMutation();

  // Hooks
  const copyToClipboard = useCopyToClipboard();

  const { deleteItem: handleDeletePlace } = useDeleteWithConfirmation({
    onDelete: async (id) => await deleteLocation({ id, level: 'Place' }).unwrap(),
    onSuccess: async () => {
      await refetch();
    },
    confirmationMessage: 'Are you sure you want to delete this place?',
    errorMessage: 'Failed to delete place',
  });

  // Define columns
  const columns: ColumnDef<PlaceWithId>[] = useMemo(() => [
    {
      accessorKey: '_id',
      header: 'ID',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: true,
      cell: ({ getValue }) => {
        const id = getValue() as string;
        const shortId = id.substring(0, 8) + '...';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={id}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {shortId}
              </Typography>
            </Tooltip>
            <IconButton size="small" onClick={() => copyToClipboard(id)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      minSize: 150,
      maxSize: 300,
      enableSorting: true,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 150,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
      cell: ({ getValue }) => {
        const type = getValue() as string;
        return (
          <Chip
            label={type}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: 'subtypes',
      header: 'Subtypes',
      size: 180,
      minSize: 150,
      maxSize: 220,
      enableSorting: false,
      cell: ({ getValue }) => {
        const subtypes = getValue() as string[] | undefined;
        if (!subtypes || subtypes.length === 0) return <Typography variant="body2">-</Typography>;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {subtypes.slice(0, 2).map((subtype, idx) => (
              <Chip
                key={idx}
                label={subtype}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            ))}
            {subtypes.length > 2 && (
              <Chip
                label={`+${subtypes.length - 2}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'tags',
      header: 'Tags',
      size: 180,
      minSize: 150,
      maxSize: 220,
      enableSorting: false,
      cell: ({ getValue }) => {
        const tags = getValue() as string[] | undefined;
        if (!tags || tags.length === 0) return <Typography variant="body2">-</Typography>;
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {tags.slice(0, 2).map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            ))}
            {tags.length > 2 && (
              <Chip
                label={`+${tags.length - 2}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
          </Box>
        );
      },
    },
    {
      accessorKey: 'neighborhood',
      header: 'Neighborhood',
      size: 150,
      minSize: 130,
      maxSize: 200,
      enableSorting: true,
      cell: ({ getValue }) => {
        const neighborhood = getValue() as any;
        const name = typeof neighborhood === 'object' && neighborhood?.name ? neighborhood.name : neighborhood;
        return (
          <Typography variant="body2" noWrap title={name}>
            {name || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      minSize: 100,
      maxSize: 140,
      enableSorting: true,
      cell: ({ getValue }) => {
        const status = getValue() as string;
        const color = status === 'public' ? 'success' : status === 'private' ? 'error' : 'warning';
        return (
          <Chip
            label={status}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      accessorKey: 'officialStatus',
      header: 'Verified',
      size: 110,
      minSize: 90,
      maxSize: 130,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const status = (getValue() as string) || 'none';
        const verifiedDate = row.original.officialVerificationDate;
        const color = status === 'verified' || status === 'admin' ? 'success' : status === 'pending' ? 'warning' : 'default';
        const label = status === 'admin' ? 'Admin' : status;
        
        return (
          <Tooltip title={verifiedDate ? `Verified: ${new Date(verifiedDate).toLocaleDateString()}` : ''}>
            <Chip
              label={label}
              size="small"
              color={color}
              variant={status === 'none' ? 'outlined' : 'filled'}
            />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'importSource',
      header: 'Source',
      size: 130,
      minSize: 110,
      maxSize: 150,
      enableSorting: true,
      cell: ({ getValue }) => {
        const source = getValue() as string | undefined;
        if (!source) return <Typography variant="body2">-</Typography>;
        
        const colorMap: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
          'user_created': 'primary',
          'google_places': 'success',
          'manual': 'secondary',
          'mta_import': 'warning',
        };
        
        const labelMap: Record<string, string> = {
          'user_created': 'User',
          'google_places': 'Google',
          'manual': 'Manual',
          'mta_import': 'MTA',
        };
        
        return (
          <Chip
            label={labelMap[source] || source}
            size="small"
            color={colorMap[source] || 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: 'userCount',
      header: 'Users',
      size: 80,
      minSize: 70,
      maxSize: 100,
      enableSorting: true,
      cell: ({ getValue }) => {
        const count = getValue() as number;
        return <Typography variant="body2">{count || 0}</Typography>;
      },
    },
    {
      accessorKey: 'address',
      header: 'Address',
      size: 200,
      minSize: 150,
      enableSorting: true,
      cell: ({ getValue }) => {
        const address = getValue() as string | undefined;
        return (
          <Typography variant="body2" noWrap title={address}>
            {address || '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Date Added',
      size: 150,
      minSize: 130,
      maxSize: 180,
      enableSorting: true,
      cell: ({ getValue }) => {
        const date = getValue() as string;
        if (!date) return <Typography variant="body2">-</Typography>;
        
        const dateObj = new Date(date);
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        const formattedTime = dateObj.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        
        return (
          <Box>
            <Typography variant="body2">{formattedDate}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formattedTime}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      minSize: 100,
      maxSize: 140,
      enableSorting: false,
      cell: ({ row }) => {
        const place = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View JSON">
              <IconButton
                size="small"
                onClick={() => setSelectedPlaceForJson(place)}
                color="primary"
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Place">
              <IconButton
                size="small"
                onClick={() => handleDeletePlace(place._id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [handleDeletePlace, copyToClipboard]);

  // Spreadsheet configuration
  const spreadsheet = useSpreadsheetConfig<Place>({
    data: places,
    columns: columns as any,
    filterableColumns: ['type', 'status', 'officialStatus', 'importSource', 'neighborhood'],
    enableFiltering: true,
    getUniqueValue: (place, column) => {
      switch (column) {
        case 'type':
          return place.type || '';
        case 'status':
          return place.status || '';
        case 'officialStatus':
          return place.officialStatus || 'none';
        case 'importSource':
          return place.importSource || 'user_created';
        case 'neighborhood': {
          return typeof place.neighborhood === 'object' && place.neighborhood?.name 
            ? place.neighborhood.name 
            : (typeof place.neighborhood === 'string' ? place.neighborhood : 'None');
        }
        default:
          return String(place[column as keyof Place] || '');
      }
    },
    filterFn: (place, filters) => {
      // Type filter
      if (filters.type && filters.type.length > 0) {
        if (!filters.type.includes(place.type)) return false;
      }
      
      // Status filter
      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(place.status)) return false;
      }
      
      // Official Status filter
      if (filters.officialStatus && filters.officialStatus.length > 0) {
        const status = place.officialStatus || 'none';
        if (!filters.officialStatus.includes(status)) return false;
      }
      
      // Import Source filter
      if (filters.importSource && filters.importSource.length > 0) {
        const source = place.importSource || 'user_created';
        if (!filters.importSource.includes(source)) return false;
      }
      
      // Neighborhood filter
      if (filters.neighborhood && filters.neighborhood.length > 0) {
        const neighborhood = typeof place.neighborhood === 'object' && place.neighborhood?.name 
          ? place.neighborhood.name 
          : (typeof place.neighborhood === 'string' ? place.neighborhood : 'None');
        if (!filters.neighborhood.includes(neighborhood)) return false;
      }
      
      return true;
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    let errorMessage = 'An unknown error occurred';
    if (typeof error === 'object' && error !== null) {
      if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
        errorMessage = String(error.data.message);
      } else if ('error' in error) {
        errorMessage = String(error.error);
      }
    }
    
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error loading places</Typography>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      p: 2,
    }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box>
          <Typography variant="h5">Places</Typography>
          <Typography variant="body2" color="text.secondary">
            {spreadsheet.filteredData.length} of {places.length} places
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Spreadsheet */}
      <Box sx={{ flex: 1, width: '100%', maxWidth: '100%', minHeight: 0 }}>
        <Spreadsheet {...spreadsheet.spreadsheetProps} />
      </Box>

      {/* JSON Display Dialog */}
      <DisplayJSON
        open={Boolean(selectedPlaceForJson)}
        onClose={() => setSelectedPlaceForJson(null)}
        data={selectedPlaceForJson}
        title="Place JSON Details"
        subtitle={selectedPlaceForJson?.name}
      />
    </Box>
  );
};

export default AllPlacesTab; 